import { STORAGE_MODULE_BASE_URL } from "../Config";

/**
 * [Low-Level] 파일을 스토리지 서버에 직접 업로드합니다.
 * Java의 `uploadFile` 메소드를 JavaScript로 구현한 것입니다.
 * 
 * @param {File} file - 업로드할 파일 객체
 * @param {string} tenantId - 테넌트 ID
 * @param {string} userId - 사용자 ID
 * @param {string} fileType - 파일 타입 (예: "IMAGE")
 * @returns {Promise<object>} StorageFileResponse에 해당하는 데이터 객체 (예: { fileId, fileName, ... })
 */
async function uploadFileToStorage(file, tenantId, userId, fileType) {
    // 1. multipart/form-data를 만들기 위해 FormData 객체를 생성합니다.
    const formData = new FormData();
    formData.append('file', file); // 'file'은 백엔드에서 @RequestParam("file")으로 받는 이름과 일치해야 합니다.
    formData.append('tenantId', tenantId);
    formData.append('userId', userId);
    formData.append('fileType', fileType);

    const url = `${STORAGE_MODULE_BASE_URL}/api/v1/storage/files/upload`;

    try {
        const response = await fetch(url, {
            method: 'POST',
            body: formData,
            // ❗ 중요: Content-Type 헤더를 직접 설정하지 마세요.
            // 브라우저가 FormData를 전송할 때 자동으로 올바른 Content-Type과 boundary를 설정해줍니다.
        });

        if (!response.ok) {
            // 서버가 4xx, 5xx 상태 코드를 반환한 경우
            const errorText = await response.text();
            throw new Error(`Storage service upload error: ${response.status} ${errorText}`);
        }

        const result = await response.json(); // ResponseVO<StorageFileResponse> 형식의 JSON 응답을 파싱

        if (result.data) {
            return result.data; // StorageFileResponse 객체 반환
        } else {
            throw new Error(`Failed to upload file to storage module: ${result.message}`);
        }

    } catch (error) {
        console.error("Error communicating with storage module for upload:", error);
        // 에러를 다시 던져서 호출한 쪽에서 처리할 수 있도록 합니다.
        throw error;
    }
}

/**
 * [High-Level] 프로필 사진을 업로드하고 최종적으로 접근 가능한 전체 URL을 반환합니다.
 * Java의 `uploadThumbnailFile` 로직을 포함합니다.
 * 
 * @param {File} profileImageFile - 업로드할 프로필 이미지 파일
 * @param {string} userId - 사용자 ID
 * @param {string} tenantId - 테넌트 ID
 * @returns {Promise<string>} 업로드된 파일에 접근할 수 있는 전체 URL
 */
export async function uploadProfileImage(profileImageFile, userId, tenantId = 'zentry_profile') {
    if (!profileImageFile) {
        throw new Error("File to upload is required.");
    }

    const fileType = "IMAGE"; // 프로필 이미지는 "IMAGE" 타입으로 고정

    // 1. Low-Level 함수를 호출하여 파일을 스토리지에 업로드합니다.
    const storageFile = await uploadFileToStorage(profileImageFile, tenantId, userId, fileType);

    // 2. 반환된 fileId를 사용하여 최종 URL을 조합합니다.
    const thumbnailUrl = `${STORAGE_MODULE_BASE_URL}/api/v1/storage/files/content/${storageFile.fileId}`;

    return thumbnailUrl;
}